import {computed, inject, Injectable} from '@angular/core';
import {SSE} from 'sse.js';
import {concatMap, firstValueFrom, Observable} from 'rxjs';
import {SseService} from './sse.service';
import {HttpClient} from '@angular/common/http';
import {SettingsStore} from '../store';
import {environment} from 'src/environments/environment';
import {ProjectRefService} from '../project-ref.service';
import {AuthenticationService} from '../authentication.service';
import {DeepAgentStreamState, StreamCrashEventType} from 'src/app/common/models/ai-reports/shared/deep-agent-state';
import {TranslateService} from '@ngx-translate/core';
import {ensureMessageInstances} from "@langchain/langgraph-sdk/ui";

export type ImageGraphEvent = Readonly<
	| [string[], 'init', { thread_id: string }]
	| [string[], 'values', DeepAgentStreamState]
	| [string[], 'messages', object]
	| [string[], StreamCrashEventType, undefined]>;

@Injectable({ providedIn: 'root' })
export class ClientImageGraphService {
	private projectRefService = inject(ProjectRefService);
	private readonly httpClient = inject(HttpClient);
	private readonly settingsStore = inject(SettingsStore);
	private readonly authService = inject(AuthenticationService);
	private readonly translateService = inject(TranslateService);
	private readonly sseService = inject(SseService);

	/**
	 * Open a streaming connection to the deep-agent endpoint.
	 *
	 * The base URL comes from the Angular environment configuration so that
	 * different deployments (development, production) can point to different
	 * API hosts without changing application code.
	 */
	chat(prompt: string, thread_id?: string, retry = false): Observable<ImageGraphEvent> {
		const url = `${environment.apiBaseUrl}${environment.endpoint.ai.deepAgentStream}`;
		const requestBody: Record<string, unknown> = {
			prompt,
			chatSettings: this.settingsStore.chatSettings(),
			...(retry ? { retry: true } : {}),
		};

		const authHeaders = computed(() => ({
			Authorization: `Bearer ${this.authService.getToken()}`,
			ProjectToken: this.projectRefService.getCurrentProjectToken(),
		}));
		const source = new SSE(url, {
			headers: {
				'Content-Type': 'application/json',
				...authHeaders(),
				thread_id: thread_id ?? '',
				'x-debug-mode': String(environment.IS_DEBUG_MODE ?? false),
				language: this.translateService.getCurrentLang() ?? environment.DEFAULT_CULTURE,
			},
			payload: JSON.stringify(requestBody),
			method: 'POST',
			start: false,
		});

		const stream$ = this.sseService.createSSEObservable<ImageGraphEvent>(source);
		return stream$.pipe(
			concatMap(async ([node, type, data]) => {
				if (type === 'values') {
					return [node, type, await this.resolveStoredMessages(data)] as const;
        }
        return [node, type, data] as ImageGraphEvent;
			})
		);
	}

	/**
	 * Deserialize LangChain Serializable messages ({lc, type: 'constructor', id, kwargs})
	 * into BaseMessage instances.
	 */
	async resolveStoredMessages({ messages, ...rest }: DeepAgentStreamState): Promise<DeepAgentStreamState> {
		return {
			messages: ensureMessageInstances(messages ?? []),
			...rest,
		};
	}

	async fetchState(thread_id: string): Promise<DeepAgentStreamState> {
		const storedState = await firstValueFrom(
			this.httpClient.get<DeepAgentStreamState>(
				`${environment.apiBaseUrl}${environment.endpoint.ai.getThread}/${thread_id}`
			)
		);
		return this.resolveStoredMessages(storedState);
	}
}
